import { useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Lock, Plus, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { filterDefined, useAutosave } from "@/hooks/use-autosave";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { ConfirmActionDialog } from "@/components/confirm-action-dialog";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { cn } from "@/lib/utils";
import type { FoodSensoryIntakeQuestionId } from "@brewbase/db/types";

type Question = RouterOutputs["food"]["sensory"]["config"]["intake"]["list"][number];
type StopsOn = "yes" | "no" | "never";

// A question stops on the opposite of its passing answer; null passes either.
const stopsOn = (passing: boolean | null): StopsOn => (passing == null ? "never" : passing ? "no" : "yes");
const passingFor = (stops: StopsOn): boolean | null => (stops === "never" ? null : stops === "no");

/**
 * The safety questions every taster answers before the consent form, the same
 * for every food tasting. A configuration editor: changes save as you go.
 */
export function IntakeQuestionsConfig() {
  const utils = trpc.useUtils();
  const { data: questions = [] } = trpc.food.sensory.config.intake.list.useQuery();
  const [dragging, setDragging] = useState<number | null>(null);
  const invalidate = () => utils.food.sensory.config.intake.list.invalidate();
  const create = trpc.food.sensory.config.intake.create.useMutation({ onSuccess: invalidate, onError: (e) => toast.error(e.message) });
  const reorder = trpc.food.sensory.config.intake.reorder.useMutation({ onSettled: invalidate });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = questions.map((q) => String(q.id));

  const dragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const from = ids.indexOf(String(e.active.id));
    const to = e.over ? ids.indexOf(String(e.over.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    const order = [...questions];
    order.splice(to, 0, ...order.splice(from, 1));
    utils.food.sensory.config.intake.list.setData(undefined, order);
    reorder.mutate(order.map((q) => q.id as FoodSensoryIntakeQuestionId));
  };

  return (
    <div className="max-w-3xl space-y-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">Safety questions</p>
        <p className="text-xs text-muted-foreground">
          Yes/no questions every taster answers after the welcome screen and before the consent form. An answer that stops
          them shows its reason and asks them to speak to the host; they can go back if it was a slip. Answers are kept
          per email, like the consent signature.
        </p>
      </div>

      {questions.length === 0 ? (
        <div className="space-y-1 border border-dashed border-olive-6 p-6 text-center">
          <ShieldCheck className="mx-auto h-6 w-6 text-olive-8" />
          <p className="text-sm">No safety questions</p>
          <p className="text-xs text-muted-foreground">Tasters go from the welcome screen straight to the consent form.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => setDragging(Number(e.active.id))}
          onDragEnd={dragEnd}
          onDragCancel={() => setDragging(null)}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {questions.map((q, i) => (
              <QuestionRow key={q.id} question={q} index={i} lifted={dragging === q.id} />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <Button variant="ghost" onClick={() => create.mutate()} disabled={create.isPending}>
        <Plus />
        Add question
      </Button>

      <Freshness />
    </div>
  );
}

function QuestionRow({ question: q, index, lifted }: { question: Question; index: number; lifted: boolean }) {
  const utils = trpc.useUtils();
  const sortable = useSortable({ id: String(q.id) });
  const id = q.id as FoodSensoryIntakeQuestionId;
  const { save } = useAutosave({
    mutation: trpc.food.sensory.config.intake.update,
    query: trpc.food.sensory.config.intake.list,
    queryInput: undefined,
    updateCache: (input, old) => old?.map((row) => (row.id === input.id ? { ...row, ...filterDefined(input) } : row)),
    onError: (err) => toast.error(err.message),
  });
  const archive = trpc.food.sensory.config.intake.archive.useMutation({
    onSuccess: () => utils.food.sensory.config.intake.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const stops = stopsOn(q.passing_answer);

  return (
    <div
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Translate.toString(sortable.transform), transition: sortable.transition }}
      className={cn("group -mt-px space-y-2 border border-olive-5 bg-white px-2 py-2.5", lifted && "relative z-10 shadow-md")}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...sortable.attributes}
          {...sortable.listeners}
          className="cursor-grab p-0.5 text-olive-7 opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label={`Move question ${index + 1}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="w-5 font-mono text-xs text-olive-11">{String(index + 1).padStart(2, "0")}</span>
        <Input
          value={q.prompt}
          placeholder="Are you over 18 years of age?"
          onChange={(e) => save({ id, prompt: e.target.value })}
          className="h-8 flex-1"
        />
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Stops on</span>
          <div
            className="inline-flex border border-olive-6"
            title={q.answered ? "Tasters have answered this question, so what stops them is fixed. Remove it and add a new one to change it." : undefined}
          >
            {(["yes", "no", "never"] as const).map((value) => (
              <button
                key={value}
                type="button"
                disabled={q.answered}
                onClick={() => value !== stops && save({ id, passing_answer: passingFor(value) })}
                className={cn(
                  "px-2.5 py-1 text-sm capitalize disabled:cursor-not-allowed",
                  stops === value ? "bg-primary text-white" : "text-olive-11 hover:bg-olive-3 disabled:hover:bg-transparent",
                )}
              >
                {value}
              </button>
            ))}
          </div>
          {q.answered && <Lock className="h-3.5 w-3.5 text-olive-8" aria-label="Fixed once answered" />}
        </div>
        <DeleteConfirmDialog
          title="Remove this question?"
          description="Tasters are no longer asked it. Answers already given to it are kept with the taster's record."
          deleteLabel="Remove"
          onConfirm={() => archive.mutate(id)}
          isDeleting={archive.isPending}
        />
      </div>
      <div className="flex items-center gap-2 pl-[3.25rem] pr-10">
        {stops === "never" ? (
          <>
            <Input
              value={q.reply_yes ?? ""}
              placeholder="Reply to yes (optional): Us too."
              onChange={(e) => save({ id, reply_yes: e.target.value || null })}
              className="h-8 flex-1 text-sm"
            />
            <Input
              value={q.reply_no ?? ""}
              placeholder="Reply to no (optional)"
              onChange={(e) => save({ id, reply_no: e.target.value || null })}
              className="h-8 flex-1 text-sm"
            />
          </>
        ) : (
          <Input
            value={q.stop_reason ?? ""}
            placeholder="Why it stops them, as the taster reads it"
            onChange={(e) => save({ id, stop_reason: e.target.value || null })}
            className="h-8 flex-1 text-sm"
          />
        )}
      </div>
    </div>
  );
}

/** How long answers are reused, and asking everyone again. */
function Freshness() {
  const { data: config } = trpc.food.sensory.config.get.useQuery();
  const [confirming, setConfirming] = useState(false);
  const { save } = useAutosave({
    mutation: trpc.food.sensory.config.intake.setValidDays,
    query: trpc.food.sensory.config.get,
    queryInput: undefined,
    updateCache: (input, old) => (old ? { ...old, intake_valid_days: input.days } : old),
    onError: (err) => toast.error(err.message),
  });
  const askAgain = trpc.food.sensory.config.intake.askEveryoneAgain.useMutation({
    onSuccess: () => {
      setConfirming(false);
      toast.success("Everyone answers the safety questions again at their next tasting.");
    },
    onError: (e) => toast.error(e.message),
  });
  if (!config) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-olive-4 pt-3 text-sm">
      <span>Ask again after</span>
      <NumericInput
        integer
        min={1}
        max={3650}
        value={config.intake_valid_days}
        onValueChange={(days) => save({ days })}
        placeholder="never"
        className="h-8 w-20"
      />
      <span>days</span>
      <span className="text-xs text-muted-foreground">Health changes; leave it blank to reuse answers for good.</span>
      <Button variant="ghost" className="ml-auto" onClick={() => setConfirming(true)}>
        <RotateCcw />
        Ask everyone again
      </Button>
      <ConfirmActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Ask everyone again?"
        description="Every answer on file stops counting, so each taster answers the safety questions again at their next tasting. Tasters already in a running tasting stay in. The old answers are kept."
        confirmLabel="Ask everyone again"
        isPending={askAgain.isPending}
        onConfirm={() => askAgain.mutate()}
      />
    </div>
  );
}
